// עץ הנתונים (05 §2): גרסאות, צמתים (binding לפי סוג), הצעות, קשרים בין עולמות.
import { z } from 'zod';
import { Id, Json, Key, Ts, rowBase } from './common.ts';
import { EtlType, ImportFilter, SyncSpec, TransformKind } from './sync.ts';

export const TreeVersionStatus = z.enum(['draft', 'published', 'retired']);
export type TreeVersionStatus = z.infer<typeof TreeVersionStatus>;

/** sys.tree_version */
export const TreeVersion = z.object({
  ...rowBase,
  worldId: Id,
  version: z.number().int().positive(),
  status: TreeVersionStatus,
  templateVersion: z.string().nullable(),
  publishedAt: Ts.nullable(),
  publishedBy: Id.nullable(),
  noteHe: z.string().nullable(),
});
export type TreeVersion = z.infer<typeof TreeVersion>;

export const TreeNodeKind = z.enum(['entity', 'field', 'relation', 'definition', 'import']);
export type TreeNodeKind = z.infer<typeof TreeNodeKind>;

export const TreeLayer = z.enum(['template', 'instance']);
export type TreeLayer = z.infer<typeof TreeLayer>;

export const Cardinality = z.enum(['1:n', 'n:1', '1:1', 'n:m']);
export type Cardinality = z.infer<typeof Cardinality>;

export const RelationVerified = z.object({
  method: z.enum(['data_match', 'dictionary_only']),
  matched: z.number().int().nonnegative(),
  attempted: z.number().int().nonnegative(),
});
export type RelationVerified = z.infer<typeof RelationVerified>;

export const EntityBinding = z.object({
  sourceTable: z.string(),
  sourceSchema: z.string().optional(),
  key: z.array(z.string()),
  /** מ-04 entity_roles, כולל 'code_table'. */
  role: z.string().optional(),
  triad: z.object({
    internalId: z.string().optional(),
    code: z.string().optional(),
    description: z.string().optional(),
    status: z.enum(['full', 'partial', 'degenerate', 'absent']),
    declaredBy: z.enum(['human', 'template']),
  }),
  companyScoped: z.boolean(),
});
export type EntityBinding = z.infer<typeof EntityBinding>;

export const FieldBinding = z.object({
  sourceColumn: z.string(),
  sourceType: z.string(),
  etlType: EtlType,
  transforms: z.array(TransformKind),
  categorical: z.boolean(),
  nullable: z.boolean(),
  triadRole: z.enum(['internal_id', 'code', 'description']).optional(),
  references: z.string().optional(),
  /** אינדקס (שדה, company) ב-etl — החלטה של מנהל בינה (מפתח למשהו אחר, חיפוש תדיר, תאריך לסינון תקופה). */
  index: z.boolean().optional(),
  /**
   * ממד אבטחה (data-access-rls): השורות של הישות מסוננות לפי הערך בשדה הזה לכל משתמש מוגבל (RLS ב-etl). אחד לכל ישות לכל
   * היותר; דורש index (השער מוסיף). הערכים המותרים לכל משתמש — ב-sys.user_data_rule, לא בעץ.
   */
  security: z.boolean().optional(),
  /** שמות הערכים של הממד (במסך בוחרים לפי שם ולא לפי קוד): ישות, שדה הקוד, שדה השם, וסינון קבוע אופציונלי. */
  securityLabels: z.object({ entity: z.string(), key: z.string(), label: z.string(), where: z.object({ column: z.string(), equals: z.string() }).optional() }).optional(),
});
export type FieldBinding = z.infer<typeof FieldBinding>;

export const RelationBinding = z.object({
  fromEntity: z.string(),
  fromField: z.string(),
  toEntity: z.string(),
  toField: z.string(),
  cardinality: Cardinality,
  verified: RelationVerified,
});
export type RelationBinding = z.infer<typeof RelationBinding>;

export const DefinitionBinding = z.object({
  textHe: z.string(),
  sqlExpr: z.string(),
  appliesTo: z.string(),
  examples: z.array(
    z.object({
      where: z.string(),
      expected: z.union([z.object({ count: z.number() }), z.object({ value: z.unknown() })]),
    }),
  ),
  status: z.enum(['proposed', 'approved']),
  source: z.enum(['template', 'instance']),
});
export type DefinitionBinding = z.infer<typeof DefinitionBinding>;

/** שדות ImportTable של 03 בלי columns. */
/** צומת import (05 §2): `ImportTable` של 03 בלי columns — `sync` (03 §2.1) במקום strategy/window/horizonMonths/everyMinutes. */
export const ImportBinding = z.object({
  enabled: z.boolean(),
  sync: SyncSpec,
  filter: ImportFilter.optional(),
  companies: z.array(z.string()),
});
export type ImportBinding = z.infer<typeof ImportBinding>;

const nodeBase = {
  ...rowBase,
  treeVersionId: Id,
  parentId: Id.nullable(),
  key: Key,
  nameHe: z.string(),
  nameEn: z.string().nullable(),
  descriptionHe: z.string().nullable(),
  layer: TreeLayer,
  evidence: Json.nullable(),
  approvedBy: Id.nullable(),
  approvedAt: Ts.nullable(),
};

export const EntityNode = z.object({ ...nodeBase, kind: z.literal('entity'), binding: EntityBinding });
export type EntityNode = z.infer<typeof EntityNode>;
export const FieldNode = z.object({ ...nodeBase, kind: z.literal('field'), binding: FieldBinding });
export type FieldNode = z.infer<typeof FieldNode>;
export const RelationNode = z.object({ ...nodeBase, kind: z.literal('relation'), binding: RelationBinding });
export type RelationNode = z.infer<typeof RelationNode>;
export const DefinitionNode = z.object({ ...nodeBase, kind: z.literal('definition'), binding: DefinitionBinding });
export type DefinitionNode = z.infer<typeof DefinitionNode>;
export const ImportNode = z.object({ ...nodeBase, kind: z.literal('import'), binding: ImportBinding });
export type ImportNode = z.infer<typeof ImportNode>;

/** sys.tree_node — union על kind. */
export const TreeNode = z.discriminatedUnion('kind', [EntityNode, FieldNode, RelationNode, DefinitionNode, ImportNode]);
export type TreeNode = z.infer<typeof TreeNode>;

/** withdrawn = בוטלה (בינה-1 או אדם) בעודה ממתינה; superseded = הוחלפה בהצעה חדשה על אותם צמתים. האישור — אדם בלבד. */
export const ProposalState = z.enum(['pending', 'approved', 'rejected', 'withdrawn', 'superseded']);
export type ProposalState = z.infer<typeof ProposalState>;

export const ProposalKind = z.enum(['add_entities', 'add_fields', 'declare_triad', 'declare_relation', 'define', 'import_change']);
export type ProposalKind = z.infer<typeof ProposalKind>;

/** sys.tree_proposal */
export const TreeProposal = z.object({
  ...rowBase,
  worldId: Id,
  baseVersion: z.number().int().nonnegative(),
  state: ProposalState,
  proposedBy: z.object({
    actor: z.string(),
    identityKind: z.enum(['agent', 'human']),
    conversationId: Id.optional(),
  }),
  kind: ProposalKind,
  rationaleHe: z.string(),
  whatChanges: Json,
  nodes: z.array(z.unknown()),
  evidence: Json,
  decidedBy: Id.nullable(),
  decidedAt: Ts.nullable(),
  noteHe: z.string().nullable(),
  supersededBy: Id.nullable(),
  requestIds: z.array(Id),
});
export type TreeProposal = z.infer<typeof TreeProposal>;

/** sys.cross_world_relation */
export const CrossWorldRelation = z.object({
  ...rowBase,
  fromWorldId: Id,
  fromEntity: z.string(),
  fromField: z.string(),
  toWorldId: Id,
  toEntity: z.string(),
  toField: z.string(),
  cardinality: Cardinality,
  verified: RelationVerified,
  approvedBy: Id.nullable(),
  approvedAt: Ts.nullable(),
});
export type CrossWorldRelation = z.infer<typeof CrossWorldRelation>;
